import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, itemImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ruler, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LETTER_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const SIZED_CATEGORIES = new Set(["top", "bottom", "outerwear", "dress", "shoes"]);
const MAX_PER_VISIT = 3;
const SKIP_DAYS = 7;
const KEY_COUNT = "threadlist.sizeprompt.count"; // sessionStorage: prompts shown this visit/session
const KEY_SKIP = "threadlist.sizeprompt.skipped"; // localStorage: { itemId: hideUntilMs }

const readSkipped = () => { try { return JSON.parse(localStorage.getItem(KEY_SKIP) || "{}"); } catch { return {}; } };

/**
 * Task 2 - missing sizes. A small floating card (not a blocking overlay) that asks for the size of ONE unsized
 * piece per visit to the Closet, at most MAX_PER_VISIT prompts per session. Save writes the size; Skip hides the
 * piece for a week. Never blocks the page.
 */
export const MissingSizePrompt = ({ items, onSaved }) => {
  const [target, setTarget] = useState(null);
  const [picked, setPicked] = useState(false); // choose at most one candidate per mount (= per visit)
  const [mode, setMode] = useState("letters"); // letters | numeric | other
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!items || picked) return;
    setPicked(true);
    const shown = Number(sessionStorage.getItem(KEY_COUNT) || 0);
    if (shown >= MAX_PER_VISIT) return;
    const skipped = readSkipped();
    const now = Date.now();
    const cand = items.find((i) => !i.size && SIZED_CATEGORIES.has(i.category) && i.listing_type !== "sold" && !(skipped[i.id] > now));
    if (!cand) return;
    sessionStorage.setItem(KEY_COUNT, String(shown + 1));
    setTarget(cand);
  }, [items, picked]);

  if (!target) return null;

  const save = async (size) => {
    const value = String(size || "").trim();
    if (!value) return;
    setSaving(true);
    try {
      await api.updateItem(target.id, { size: value });
      onSaved?.(target.id, value);
      toast.success(`Size ${value} saved`);
      setTarget(null);
    } catch { toast.error("Could not save the size"); }
    finally { setSaving(false); }
  };
  const skip = () => {
    const skipped = readSkipped();
    skipped[target.id] = Date.now() + SKIP_DAYS * 86400000;
    localStorage.setItem(KEY_SKIP, JSON.stringify(skipped));
    setTarget(null);
  };

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} transition={{ duration: 0.2 }}
        className="fixed right-4 bottom-20 md:bottom-6 z-40 w-[calc(100vw-2rem)] max-w-[340px] rounded-2xl border border-border bg-card shadow-lift p-3.5"
        role="dialog" aria-label="Add a missing size" data-testid="missing-size-prompt"
      >
        <button onClick={skip} className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Skip" data-testid="missing-size-close"><X className="h-4 w-4" /></button>
        <div className="flex items-start gap-3">
          <div className="h-16 w-14 shrink-0 rounded-xl overflow-hidden bg-white border border-border">
            <img src={itemImage(target)} alt={target.name || "garment"} className="h-full w-full object-contain" />
          </div>
          <div className="min-w-0 pr-6">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1"><Ruler className="h-3 w-3" /> Missing size</p>
            <p className="text-sm font-semibold leading-tight mt-0.5 truncate" data-testid="missing-size-item-name">{target.name || target.subtype || "Item"}</p>
            <p className="text-[11px] text-muted-foreground truncate capitalize">{[target.brand, (target.subcategory || target.category || "").replace(/_/g, " ")].filter(Boolean).join(" · ")}</p>
          </div>
        </div>

        {mode === "letters" && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="missing-size-chips">
            {LETTER_SIZES.map((s) => (
              <button key={s} onClick={() => save(s)} disabled={saving} className="h-8 min-w-[40px] px-2.5 rounded-full bg-secondary text-xs font-mono-num font-semibold hover:bg-foreground hover:text-background disabled:opacity-50 transition-colors" data-testid={`missing-size-chip-${s}`}>{s}</button>
            ))}
            <button onClick={() => setMode("numeric")} className="h-8 px-2.5 rounded-full border border-border text-xs hover:bg-secondary transition-colors" data-testid="missing-size-numeric">Numeric</button>
            <button onClick={() => setMode("other")} className="h-8 px-2.5 rounded-full border border-border text-xs hover:bg-secondary transition-colors" data-testid="missing-size-other">Other</button>
          </div>
        )}
        {mode !== "letters" && (
          <form className="mt-3 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); save(custom); }}>
            <Input autoFocus value={custom} onChange={(e) => setCustom(e.target.value)} inputMode={mode === "numeric" ? "decimal" : "text"} placeholder={mode === "numeric" ? (target.category === "shoes" ? "e.g. 10.5" : "e.g. 32 or 32x30") : "e.g. One size, 42R"} className="h-9 rounded-full" data-testid="missing-size-input" />
            <Button type="submit" size="sm" disabled={saving || !custom.trim()} className="rounded-full h-9" data-testid="missing-size-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            <button type="button" onClick={() => { setMode("letters"); setCustom(""); }} className="text-xs text-muted-foreground hover:text-foreground whitespace-nowrap" data-testid="missing-size-back">Letters</button>
          </form>
        )}
        <div className="mt-2.5 flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">Helps sizing for outfits and listings.</p>
          <button onClick={skip} className="text-xs text-muted-foreground hover:text-foreground" data-testid="missing-size-skip">Skip</button>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
};
